import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Check, Headphones, Play, Zap, Shield,
  Star, Radio, Crown, X, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { foundingJustSoldOut, FOUNDING_SOLDOUT_WINDOW_MS } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { icon: Headphones, text: 'Všechny podcasty a videa na jednom místě' },
  { icon: Zap,        text: 'Jedno předplatné místo několika' },
  { icon: Shield,     text: 'Bez reklam' },
  { icon: Play,       text: 'Exkluzivní obsah tvůrců' },
  { icon: Star,       text: 'Bez skrytých poplatků' },
];

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [, setTick] = useState(0);   // forces the "Vyprodáno" badge to expire without a reload

  useEffect(() => {
    axios.get(`${API}/subscriptions/founding-status`)
      .then(r => setFoundingStatus(r.data))
      .catch(() => {});
  }, []);

  const remaining = foundingStatus?.remaining ?? null;
  const foundingAvailable = remaining === null || remaining > 0;
  const justSoldOut = foundingJustSoldOut(foundingStatus);

  // When we're inside the post-sell-out window, schedule one re-render at its end
  // so the "Vyprodáno" badge disappears on its own.
  useEffect(() => {
    if (!justSoldOut || !foundingStatus?.sold_out_at) return;
    const left = FOUNDING_SOLDOUT_WINDOW_MS - (Date.now() - Date.parse(foundingStatus.sold_out_at));
    if (left <= 0) return;
    const id = setTimeout(() => setTick(t => t + 1), left + 250);
    return () => clearTimeout(id);
  }, [justSoldOut, foundingStatus]);

  const goRegister = () => navigate(isAuthenticated ? '/dashboard' : '/register');
  const goSubscribe = (hash = '') =>
    navigate(isAuthenticated ? `/subscribe${hash}` : `/register`);

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ─────────────────────────── 1. HERO ─────────────────────────────── */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        {/* subtle radial glow behind text */}
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(ellipse 70% 55% at 50% 0%, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
          }}
        />

        <div className="container-app max-w-3xl text-center mx-auto px-4">
          {/* founding badge */}
          {foundingAvailable && remaining !== null && (
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-4 py-1.5 text-sm font-medium text-yellow-400 mb-6">
              <Star className="w-3.5 h-3.5 fill-yellow-400" />
              Pouze prvních 199 lidí získá cenu 199 Kč/měsíc garantovanou na 12 měsíců
              {remaining < 199 && (
                <span className="ml-1 font-semibold text-yellow-300">
                  — zbývá {remaining}
                </span>
              )}
            </div>
          )}
          {justSoldOut && (
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-1.5 text-sm font-medium text-muted-foreground mb-6">
              <X className="w-3.5 h-3.5" />
              Zakládající místa za 199 Kč jsou vyprodaná — nyní 299 Kč/měsíc
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight tracking-tight mb-6">
            Jedno předplatné.{' '}
            <span className="text-primary">Všichni tvůrci.</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto mb-4">
            Už žádné placení několika platforem.
            Onesub spojuje exkluzivní obsah tvůrců na jedno místo.
          </p>

          <p className="text-sm text-muted-foreground mb-3">
            Podcasty, videa a exkluzivní obsah bez reklam, na jednom místě.
          </p>

          <p className="text-sm text-muted-foreground/60 mb-10">
            Přístup ke všem tvůrcům&nbsp;•&nbsp;Bez reklam&nbsp;•&nbsp;Bez skrytých poplatků
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={goRegister}
              className="bg-primary text-white hover:bg-primary/90 px-8 py-6 text-base font-semibold shadow-lg shadow-primary/20"
              data-testid="hero-cta-primary"
            >
              Získat přístup
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-6 text-base border-border hover:bg-secondary"
              data-testid="hero-cta-secondary"
            >
              Prozkoumat tvůrce
            </Button>
          </div>

          <p className="text-sm text-muted-foreground/80 mt-5 font-medium">
            Zrušit kdykoliv. Bez závazků.
          </p>
        </div>
      </section>

      {/* ─────────────────────────── 2. PROBLÉM ─────────────────────────── */}
      <section className="py-16 md:py-20 bg-card/40 border-y border-border">
        <div className="container-app max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">

            {/* Dnes */}
            <div className="rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <X className="w-3.5 h-3.5 text-red-400" />
                </div>
                <span className="font-semibold text-foreground">Dnes</span>
              </div>
              <ul className="space-y-3 text-muted-foreground text-sm">
                {[
                  'Platíš 3–5 různých předplatných',
                  'Obsah je rozdělený napříč platformami',
                  'Přicházíš o exkluzivní epizody',
                ].map(t => (
                  <li key={t} className="flex items-start gap-2.5">
                    <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Onesub */}
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="font-semibold text-foreground">Onesub</span>
              </div>
              <ul className="space-y-3 text-muted-foreground text-sm">
                {[
                  'Jedno předplatné',
                  'Vše na jednom místě',
                  'Okamžitý přístup ke všemu',
                ].map(t => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ─────────────────────── 3. JAK TO FUNGUJE ──────────────────────── */}
      <section className="py-16 md:py-24">
        <div className="container-app max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Jak to funguje</h2>
          <p className="text-center text-muted-foreground mb-12 text-sm">Jednoduché</p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: '1', icon: Crown,      title: 'Zaplatíš jedno předplatné',       desc: 'Měsíční platba — méně než kdybys platil za dva podcasty zvlášť.' },
              { n: '2', icon: Radio,      title: 'Získáš přístup ke všem tvůrcům',  desc: 'Celá knihovna. Všechny epizody. Okamžitě.' },
              { n: '3', icon: Headphones, title: 'Posloucháš bez omezení',           desc: 'Bez reklam, bez přeskakování, bez kompromisů.' },
            ].map(s => (
              <div key={s.n} className="flex flex-col items-center text-center gap-4 p-6 rounded-2xl border border-border bg-card hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                  {s.n}
                </div>
                <s.icon className="w-6 h-6 text-primary" />
                <h3 className="font-semibold text-foreground">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────── 4. PROČ ONESUB ────────────────────────── */}
      <section className="py-16 md:py-24">
        <div className="container-app max-w-2xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">Proč Onesub</h2>

          <ul className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4.5 h-4.5 text-primary" />
                </div>
                <span className="font-medium text-foreground">{text}</span>
                <Check className="w-4 h-4 text-primary ml-auto flex-shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─────────────────────────── 6. PRICING ─────────────────────────── */}
      <section className="py-16 md:py-24 bg-card/40 border-y border-border">
        <div className="container-app max-w-lg mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">
            Odemkni všechny podcasty
          </h2>
          {foundingAvailable && remaining !== null && (
            <p className="text-center text-yellow-400 text-sm font-medium mb-8">
              ⭐ Pouze prvních 199 uživatelů má 199 Kč/měsíc garantovanou na 12 měsíců
              {remaining < 199 && ` — zbývá ${remaining} míst`}
            </p>
          )}
          {justSoldOut && (
            <p className="text-center text-muted-foreground text-sm font-medium mb-8">
              Zakládající místa za 199 Kč jsou vyprodaná — nyní 299 Kč/měsíc
            </p>
          )}
          {!foundingAvailable && !justSoldOut && <div className="mb-8" />}

          {/* Trial card — highlighted */}
          <div className="relative rounded-2xl border-2 border-primary bg-primary/5 p-6 md:p-8 mb-4">
            <div className="absolute -top-3.5 left-6">
              <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                Doporučujeme
              </span>
            </div>

            <p className="text-xs text-muted-foreground mb-1 mt-1">Vyzkoušej vše na 7 dní</p>
            <div className="flex items-end gap-2 mb-1">
              <span className="text-5xl font-extrabold text-primary">99</span>
              <span className="text-muted-foreground text-base mb-1.5">Kč</span>
              <span className="text-muted-foreground text-sm mb-1.5">≈ 14 Kč / den</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Poté{' '}
              <span className="text-foreground font-semibold">
                {foundingAvailable ? '199' : '299'} Kč / měsíc
              </span>
              {foundingAvailable ? ' garantovanou na 12 měsíců' : ''} — zrušit lze kdykoliv
            </p>

            <ul className="space-y-2 mb-6">
              {[
                'Přístup ke všem tvůrcům',
                'Neomezený poslech',
                'Bez reklam',
                'Nové epizody ihned',
              ].map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Button
              className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-base font-semibold shadow-lg shadow-primary/20"
              onClick={() => goSubscribe('#trial')}
            >
              Získat přístup na 7 dní
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <p className="text-xs text-muted-foreground/60 text-center mt-3">
              Zrušit kdykoliv • Bez závazků
            </p>
          </div>

          {/* Secondary — full price */}
          <button
            onClick={() => goSubscribe()}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-3 flex items-center justify-center gap-1.5"
          >
            Nechci trial, chci plný přístup hned
            <ChevronRight className="w-4 h-4" />
            <span className="font-semibold text-foreground">
              {foundingAvailable ? '199' : '299'} Kč / měsíc
            </span>
          </button>
        </div>
      </section>

      {/* ─────────────────────────── 7. FINAL CTA ───────────────────────── */}
      <section className="py-20 md:py-28 text-center">
        <div className="container-app max-w-xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
            Přestaň platit za každý podcast zvlášť
          </h2>
          <p className="text-muted-foreground mb-10 text-base">
            Získej přístup ke všem na jednom místě.
          </p>
          <Button
            size="lg"
            onClick={goRegister}
            className="bg-primary text-white hover:bg-primary/90 px-10 py-6 text-base font-semibold shadow-lg shadow-primary/20"
          >
            Získat přístup
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          {foundingAvailable && remaining !== null && (
            <p className="text-xs text-yellow-400/80 mt-4">
              ⭐ Ještě {remaining} {remaining === 1 ? 'místo' : remaining < 5 ? 'místa' : 'míst'} za 199 Kč/měsíc garantovanou na 12 měsíců
            </p>
          )}
          {justSoldOut && (
            <p className="text-xs text-muted-foreground mt-4">
              Zakládající místa za 199 Kč jsou vyprodaná — nyní 299 Kč/měsíc
            </p>
          )}
        </div>
      </section>

      {/* ─────────────────────────── FOOTER ─────────────────────────────── */}
      <footer className="py-10 border-t border-border">
        <div className="container-app flex flex-col md:flex-row items-center justify-between gap-3 px-4">
          <div className="text-xl font-bold text-foreground">OneSub</div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/pro-tvurce" className="hover:text-foreground transition-colors">Pro tvůrce</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Podmínky</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Soukromí</Link>
          </div>
          <p className="text-xs text-muted-foreground/50">
            © {new Date().getFullYear()} OneSub. Všechna práva vyhrazena.
          </p>
        </div>
      </footer>

    </div>
  );
}
